import { first, last } from 'lodash-es';
import { getCalendarContextView, getShowInlineMemory } from 'redux/selectors/calendar';
import { getFilter as getCalendarFilter } from 'redux/selectors/calendar.filter';
import { getRouteSegments, getViewFromRoute } from 'redux/selectors/routing';
import { getMemoryViewmode } from 'redux/selectors/timeline';
import { getUserId, getUserProperties } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { getForecastsViewType } from 'routes/forecast/redux/modules/forecast.selectors';
import type { ForecastDraft } from 'routes/forecast/types/forecast';
import type { HourAnalyticsSaveContext, TaskAnalyticsSaveContext } from 'types/analytics';
import type { HourDraft } from 'types/hour';
import { memorySidebarIsVisible } from 'utils/timeline';

export function finalizeHourAnalyticsContext(
  { context, user, timer_state }: Pick<HourDraft, 'context' | 'user' | 'timer_state'>,
  state: IState
): HourAnalyticsSaveContext {
  const currentUserId = getUserId(state);
  const view_context = getViewFromRoute(state);
  const calendar_view = view_context === 'Calendar' ? getCalendarContextView(state) : undefined;
  const has_timer = timer_state === 'start';
  const { multiUserMode } = getCalendarFilter(state);
  const properties = getUserProperties(state);
  const showMemorySidebar =
    view_context === 'Calendar' &&
    (memorySidebarIsVisible({
      userId: currentUserId,
      draftUserId: user?.id,
      multiUserMode,
      properties,
    }) ||
      getShowInlineMemory(state));
  const memoryViewmode = getMemoryViewmode(state);
  const memory_experience = memoryViewmode.tic ? 'New' : 'Old';
  const memory_view = !showMemorySidebar
    ? 'Hidden'
    : memoryViewmode.viewmode === 'timeline'
      ? 'Timeline'
      : 'List';

  return {
    ...context,
    view_context,
    memory_experience,
    memory_view,
    calendar_view,
    has_timer,
  };
}

export function finalizeTaskAnalyticsContext(
  { context }: Pick<ForecastDraft, 'context'>,
  state: IState
): TaskAnalyticsSaveContext {
  let view_context = getViewFromRoute(state);
  const viewType = getForecastsViewType(state);

  let tasks_scope: TaskAnalyticsSaveContext['tasks_scope'] | undefined;
  const routeSegments = getRouteSegments(state);
  const firstRouteSegment = first(routeSegments);
  const lastRouteSegment = last(routeSegments);

  if (firstRouteSegment === 'tasks') {
    const [_, subRoute] = routeSegments;
    tasks_scope = subRoute === 'users' ? 'Users' : 'Projects';
  } else if (firstRouteSegment === 'users' && lastRouteSegment === 'tasks') {
    view_context = 'Tasks';
    tasks_scope = 'User';
  } else if (firstRouteSegment === 'projects' && lastRouteSegment === 'tasks') {
    view_context = 'Tasks';
    tasks_scope = 'Project';
  }

  const tasks_view =
    view_context === 'Tasks'
      ? viewType === 'list'
        ? 'List'
        : viewType === 'timeline'
          ? 'Timeline'
          : undefined
      : undefined;

  return {
    ...context,
    view_context,
    tasks_scope,
    tasks_view,
  };
}
